import { useEffect, useState } from "react";
import {
  Check,
  Copy,
  EyeOff,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import HeatmapDots from "@/components/RefClick/HeatmapDots";
import TodayProgress from "@/components/RefClick/TodayProgress";
import EarningsSection from "@/components/RefClick/EarningsSection";
import { useNow } from "@/components/RefClick/TickProvider";
import { useAuth } from "@/components/RefClick/AuthProvider";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  CUSTOM_FIELD_PRESETS,
  copyToClipboard,
  formatElapsed,
  isSensitiveLabel,
  isValidHttpUrl,
  monogramStyle,
  monogramText,
} from "@/lib/refclick-utils";
import { serverToClientMs } from "@/lib/clock-sync";

/**
 * Slide-over that opens when the user clicks a link card's body.
 *
 * Keeps four sections tight and compact:
 *   1. Compact stats — today's count, last-click time, 7-day mini heatmap
 *   2. Editable name, URL, daily target (inline validation)
 *   3. Custom fields — free-form label/value pairs with quick-add chips
 *      for Email / Username / Password / Notes and a one-click copy
 *   4. Footer — Delete (destructive) + Save
 *
 * The parent controls `open`. When `open` flips true with a different
 * `link.id`, the local form state re-initialises from the new link.
 */
export default function LinkDetail({
  link,
  open,
  onOpenChange,
  onSave,
  onDelete,
  onEarningsChanged,
}) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [target, setTarget] = useState(5);
  const [rate, setRate] = useState(25);
  const [fields, setFields] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  // Feb-2026: fetch the random-targets setting so the daily-target
  // input can show a small hint when the manual value is currently
  // overridden by today's roll. Local to this modal — the main
  // dashboard also refetches settings via the Dashboard poll, but
  // duplicating here keeps LinkDetail self-sufficient.
  const [randomTargetsOn, setRandomTargetsOn] = useState(false);
  useEffect(() => {
    let cancelled = false;
    // Lazy import to avoid pulling statsApi at module scope (LinkDetail
    // is rendered eagerly; this fetch only matters when the modal opens).
    import("@/lib/stats-api")
      .then(({ statsApi }) => statsApi.getSettings())
      .then((s) => {
        if (!cancelled) setRandomTargetsOn(!!s?.random_targets_enabled);
      })
      .catch(() => {
        /* setting unavailable — hide the hint quietly */
      });
    return () => {
      cancelled = true;
    };
  }, [link?.id]);

  // Auth state — sensitive fields (label "Password") are masked when locked.
  const { unlocked, promptForPin } = useAuth();

  // Live-ticking "last click" label — shares the app-wide 1s tick.
  // Server timestamp is converted to the client clock domain via
  // clock-sync so a server ↔ client skew doesn't yield "14s ago"
  // right after a fresh click.
  const now = useNow();
  const elapsedLabel = (() => {
    if (!link || !link.last_click_at) return null;
    if ((link.today_count || 0) === 0) return null;
    const serverMs = new Date(link.last_click_at).getTime();
    if (!serverMs) return null;
    const clientMs = serverToClientMs(serverMs);
    return formatElapsed(new Date(clientMs).toISOString(), now);
  })();

  // Responsive layout: bottom sheet on phones (thumb-first), side panel on
  // wider screens. Sheet primitive supports both via the `side` prop.
  const isMobile = useMediaQuery("(max-width: 639px)");

  const currentId = link?.id;

  // Re-init form state ONLY when the identity of the shown link changes.
  // This avoids clobbering the user's in-flight edits every time the poll
  // returns a fresh copy of the same link.
  useEffect(() => {
    if (!link) return;
    setName(link.name || "");
    setUrl(link.url || "");
    setTarget(link.daily_target || 5);
    setRate(
      link.usd_per_click === 0 || link.usd_per_click ? link.usd_per_click : 25,
    );
    setFields(
      (Array.isArray(link.custom_fields) ? link.custom_fields : []).map(
        (f, i) => ({
          _key: `${currentId}-${i}-${Math.random().toString(36).slice(2, 8)}`,
          label: (f && f.label) || "",
          value: (f && f.value) || "",
        }),
      ),
    );
    setErrors({});
    setSaving(false);
  }, [currentId]);

  const addField = (presetLabel = "") => {
    setFields((f) => [
      ...f,
      {
        _key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        label: presetLabel,
        value: "",
      },
    ]);
  };

  const updateField = (key, patch) =>
    setFields((f) => f.map((x) => (x._key === key ? { ...x, ...patch } : x)));

  const removeField = (key) =>
    setFields((f) => f.filter((x) => x._key !== key));

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = "Name is required.";
    if (!isValidHttpUrl(url.trim()))
      e.url = "Must be a valid http:// or https:// URL.";
    const t = Number(target);
    if (!Number.isInteger(t) || t < 1) e.target = "Target must be ≥ 1.";
    const r = Number(rate);
    if (!Number.isFinite(r) || r < 0 || r > 10000) {
      e.rate = "$ per click must be between 0 and 10000.";
    }
    if (fields.some((f) => !f.label.trim()))
      e.fields = "Every custom field needs a label.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        url: url.trim(),
        daily_target: Number(target),
        usd_per_click: Number(rate),
        custom_fields: fields.map((f) => ({
          label: f.label.trim(),
          value: f.value,
        })),
      });
    } catch {
      // Parent surfaces server-side validation via toast; keep sheet open.
    } finally {
      setSaving(false);
    }
  };

  if (!link) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        data-testid="link-detail"
        data-orientation={isMobile ? "bottom" : "right"}
        className={
          "flex flex-col gap-0 border-white/10 bg-[#0a0a0c] p-0 text-text-main " +
          (isMobile
            ? "h-[92vh] w-full max-w-full rounded-t-2xl"
            : "w-full sm:max-w-md")
        }
      >
        {/* Header — compact identity strip */}
        <SheetHeader className="space-y-2 border-b border-white/5 px-5 pb-4 pt-6 text-left">
          <div className="flex items-center gap-3">
            <div
              aria-hidden
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg font-display text-base font-bold text-white/95"
              style={monogramStyle(link.name)}
            >
              {monogramText(link.name)}
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              <SheetTitle
                className="truncate font-display text-lg font-bold tracking-tight text-text-main"
                data-testid="detail-title"
              >
                {link.name}
              </SheetTitle>
              <SheetDescription className="font-mono-safe text-[11px] text-text-muted">
                Details · custom fields · stats
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {/* Section 1 — compact stats */}
          <div
            className="mb-5 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3"
            data-testid="detail-stats"
          >
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-text-muted">
                  Today
                </div>
                <div className="mt-1 font-mono-num text-2xl leading-none">
                  <span
                    className={
                      link.target_met ? "text-accent" : "text-text-main"
                    }
                  >
                    {link.today_count}
                  </span>
                  <span className="text-text-muted">
                    /{link.effective_daily_target || link.daily_target}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-text-muted">
                  Last click
                </div>
                <div
                  className="mt-1 font-mono-safe text-sm text-text-main"
                  data-testid="detail-last-click"
                >
                  {elapsedLabel || "—"}
                </div>
              </div>
            </div>
            <div className="mt-3">
              <TodayProgress
                count={link.today_count}
                target={link.effective_daily_target || link.daily_target}
              />
            </div>
            <div className="mt-4 flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-text-muted">
                Last 7d
              </span>
              <HeatmapDots
                days={Array.isArray(link.last_7_days) ? link.last_7_days : []}
                target={link.effective_daily_target || link.daily_target}
              />
            </div>
          </div>

          {/* Section 2 — editable fields */}
          <div className="mb-5 flex flex-col gap-4">
            <FieldGroup label="Name" error={errors.name} testid="err-detail-name">
              <Input
                data-testid="detail-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
                className="border-white/10 bg-black/30 text-text-main focus-visible:border-accent focus-visible:ring-accent/50"
              />
            </FieldGroup>

            <FieldGroup label="URL" error={errors.url} testid="err-detail-url">
              <div className="flex gap-2">
                <Input
                  data-testid="detail-url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  inputMode="url"
                  className="font-mono-safe border-white/10 bg-black/30 text-sm text-text-main focus-visible:border-accent focus-visible:ring-accent/50"
                />
                <CopyButton value={url} testid="detail-url-copy" />
              </div>
            </FieldGroup>

            <FieldGroup
              label="Daily target"
              error={errors.target}
              testid="err-detail-target"
            >
              <Input
                data-testid="detail-target"
                type="number"
                min={1}
                max={999}
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="w-28 border-white/10 bg-black/30 font-mono-num text-text-main focus-visible:border-accent focus-visible:ring-accent/50"
              />
              {randomTargetsOn && (
                <p
                  data-testid="detail-target-hint"
                  className="mt-1 font-mono-safe text-[11px] leading-snug text-text-muted"
                >
                  Random daily targets is <span className="text-accent">on</span> —
                  this value is used when it&rsquo;s off.
                </p>
              )}
            </FieldGroup>

            <FieldGroup
              label="$ per click"
              error={errors.rate}
              testid="err-detail-rate"
            >
              <div className="flex items-center gap-3">
                <div className="relative w-28">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 font-mono-num text-xs text-text-muted/70">
                    $
                  </span>
                  <Input
                    data-testid="detail-rate"
                    type="number"
                    min={0}
                    max={10000}
                    step="0.01"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    className="border-white/10 bg-black/30 pl-5 font-mono-num text-text-main focus-visible:border-accent focus-visible:ring-accent/50"
                  />
                </div>
                <span className="font-mono-safe flex-1 text-[11px] leading-snug text-text-muted/85">
                  Added to earnings on every +1. Set 0 to disable.
                </span>
              </div>
            </FieldGroup>
          </div>

          {/* Section 3 — custom key-value fields */}
          <div className="mb-2">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.22em] text-text-muted">
                Custom fields
              </h3>
              <span className="font-mono-num text-[11px] text-text-muted">
                {fields.length}
              </span>
            </div>

            {/* Preset quick-add chips */}
            <div className="mb-3 flex flex-wrap gap-1.5" data-testid="detail-presets">
              {CUSTOM_FIELD_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => addField(preset)}
                  data-testid={`detail-preset-${preset.toLowerCase()}`}
                  className="focus-accent inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-xs text-text-muted transition-colors hover:border-accent/40 hover:bg-accent/10 hover:text-accent"
                >
                  <Plus className="h-3 w-3" />
                  {preset}
                </button>
              ))}
              <button
                type="button"
                onClick={() => addField("")}
                data-testid="detail-preset-custom"
                className="focus-accent inline-flex items-center gap-1 rounded-full border border-dashed border-white/15 bg-transparent px-2.5 py-1 text-xs text-text-muted transition-colors hover:border-accent/40 hover:text-accent"
              >
                <Plus className="h-3 w-3" />
                Custom
              </button>
            </div>

            {/* Field list */}
            <div className="flex flex-col gap-2" data-testid="detail-fields">
              {fields.length === 0 && (
                <p className="rounded-lg border border-dashed border-white/10 px-3 py-3 text-center font-mono-safe text-[11px] text-text-muted">
                  No custom fields yet. Add Email, Username, etc. above.
                </p>
              )}
              {fields.map((f, i) => {
                const isSensitive = isSensitiveLabel(f.label);
                const shouldMask = isSensitive && !unlocked;
                return (
                  <div
                    key={f._key}
                    data-testid={`detail-field-${i}`}
                    data-sensitive={isSensitive ? "true" : "false"}
                    data-masked={shouldMask ? "true" : "false"}
                    className="flex items-start gap-1.5 rounded-lg border border-white/5 bg-white/[0.015] p-2"
                  >
                    <div className="flex flex-1 flex-col gap-1.5">
                      <Input
                        value={f.label}
                        onChange={(e) =>
                          updateField(f._key, { label: e.target.value })
                        }
                        placeholder="Label"
                        maxLength={40}
                        data-testid={`detail-field-${i}-label`}
                        className="h-8 border-white/10 bg-black/30 text-xs text-text-main focus-visible:border-accent focus-visible:ring-accent/50"
                      />
                      {shouldMask ? (
                        <button
                          type="button"
                          onClick={() =>
                            promptForPin().catch(() => {})
                          }
                          data-testid={`detail-field-${i}-value-masked`}
                          aria-label="Unlock to reveal password"
                          className="focus-accent flex h-8 items-center gap-2 rounded-md border border-white/10 bg-black/40 px-3 text-left font-mono-safe text-xs tracking-[0.3em] text-text-muted hover:border-accent/40 hover:text-accent"
                        >
                          <EyeOff className="h-3.5 w-3.5" />
                          <span>••••••••</span>
                          <span className="ml-auto text-[10px] uppercase tracking-[0.18em] text-text-muted/70">
                            Unlock
                          </span>
                        </button>
                      ) : (
                        <Input
                          value={f.value}
                          onChange={(e) =>
                            updateField(f._key, { value: e.target.value })
                          }
                          placeholder="Value"
                          type={isSensitive ? "text" : "text"}
                          data-testid={`detail-field-${i}-value`}
                          className="font-mono-safe h-8 border-white/10 bg-black/30 text-xs text-text-main focus-visible:border-accent focus-visible:ring-accent/50"
                        />
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <CopyButton
                        value={shouldMask ? "" : f.value}
                        disabled={shouldMask}
                        small
                        testid={`detail-field-${i}-copy`}
                      />
                      <button
                        type="button"
                        onClick={() => removeField(f._key)}
                        data-testid={`detail-field-${i}-remove`}
                        aria-label="Remove field"
                        className="focus-accent flex h-8 w-8 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-red-500/10 hover:text-red-300"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {errors.fields && (
              <p
                className="font-mono-safe mt-2 text-[11px] text-red-400"
                data-testid="err-detail-fields"
              >
                {errors.fields}
              </p>
            )}
          </div>

          {/* Section 4 — earnings (Phase 3). Renders only when the link
              exists (i.e. after create). We pass the freshest link id so
              the section can list/add/delete without waiting on save. */}
          {link?.id && (
            <div className="mb-2">
              <EarningsSection link={link} onChanged={onEarningsChanged} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-white/5 bg-black/40 px-5 py-4">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onDelete && onDelete(link)}
            data-testid="detail-delete"
            className="gap-1.5 text-red-300 hover:bg-red-500/10 hover:text-red-200"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            data-testid="detail-save"
            className="border border-accent/40 bg-accent/20 text-accent hover:bg-accent/30"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ------------------------------------------------------------------ */
/* Small internal components — kept local to keep this file cohesive. */
/* ------------------------------------------------------------------ */

function FieldGroup({ label, children, error, testid }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[10px] font-bold uppercase tracking-[0.22em] text-text-muted">
        {label}
      </Label>
      {children}
      {error && (
        <span
          data-testid={testid}
          className="font-mono-safe text-[11px] text-red-400"
        >
          {error}
        </span>
      )}
    </div>
  );
}

function CopyButton({ value, small = false, testid, disabled = false }) {
  const [copied, setCopied] = useState(false);
  const handle = async (e) => {
    e.stopPropagation();
    if (disabled || !value) return;
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    }
  };
  const size = small ? "h-8 w-8" : "h-9 w-9";
  return (
    <button
      type="button"
      onClick={handle}
      disabled={disabled}
      data-testid={testid}
      aria-label={disabled ? "Unlock to copy" : copied ? "Copied" : "Copy value"}
      className={
        "focus-accent flex " +
        size +
        " shrink-0 items-center justify-center rounded-md border border-white/10 bg-white/[0.03] text-text-muted transition-colors hover:border-accent/40 hover:bg-accent/10 hover:text-accent disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/10 disabled:hover:bg-white/[0.03] disabled:hover:text-text-muted"
      }
    >
      {copied ? (
        <Check className="h-3.5 w-3.5" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

